import type { JoinPageSection } from '~/utils/joinTracking'
import { buildJoinParams } from '~/utils/joinTracking'
import { pushCheckoutStepView, pushCheckoutStepCompleted } from '~/utils/gtm'
import type { PageSection } from '~/utils/checkoutTracking'

/**
 * Emits the Join funnel to the dataLayer (AB#64104).
 *
 * Mirrors useCheckoutTracking: same push helpers, same params, Join `page_section` values.
 * Each page reports its own section — call `trackStepView` once the page has decided to
 * render (after its guards), and `trackStepCompleted` from the handler that moves the
 * applicant forward. Nothing here holds state between calls.
 */
export function useJoinTracking () {
  const { proxy: gtm } = useScriptGoogleTagManager()
  const { userRole } = useSAuthState()
  const route = useRoute()

  // The push helpers are typed against the checkout PageSection union. Join sections are a
  // disjoint set of strings that the same schema field carries, so they are widened at the
  // boundary here rather than polluting the checkout union with Join members — a checkout
  // renderer must not be able to pass 'join_vat' to trackStepView.
  const asPageSection = (section: JoinPageSection) => section as unknown as PageSection
  const getParams = () => buildJoinParams(userRole.value, route.path)

  /** The applicant is looking at this section: call once the page has decided to render. */
  const trackStepView = (section: JoinPageSection) => {
    pushCheckoutStepView(gtm, getParams(), asPageSection(section))
  }

  /** The applicant finished this section: call from the handler that moves them on. */
  const trackStepCompleted = (section: JoinPageSection) => {
    pushCheckoutStepCompleted(gtm, getParams(), asPageSection(section))
  }

  return { getParams, trackStepView, trackStepCompleted }
}
